import { useLayoutEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { resolveLoginFormError } from "../../lib/authFormErrors";
import { isPasswordResetEnabled } from "../../lib/passwordResetEnabled";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const { login } = useAuthStore();
  const isWakingUp = useAuthStore((state) => state.isWakingUp);
  const { t } = useTranslation();

  const loginSchema = z.object({
    email: z.string().email(t("login.invalidEmail")),
    password: z.string().min(1, t("login.passwordRequired")),
  });

  type LoginForm = z.infer<typeof loginSchema>;

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const { ref: passwordRegisterRef, ...passwordField } = register("password");

  useLayoutEffect(() => {
    if (submitError) {
      passwordRef.current?.focus();
    }
  }, [submitError]);

  const onSubmit = async (data: LoginForm) => {
    setSubmitError(null);
    setIsLoading(true);
    try {
      await login(data.email, data.password);
      toast.success(t("login.welcome"));
    } catch (error) {
      const resolved = resolveLoginFormError(error, { isWakingUp });
      const email = getValues("email");
      setValue("email", email);
      setValue("password", "");
      if (resolved) {
        setSubmitError(t(resolved.messageKey));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const emailDescribedBy = [
    errors.email ? "login-email-error" : null,
    submitError ? "login-submit-error" : null,
  ]
    .filter(Boolean)
    .join(" ") || undefined;

  const passwordDescribedBy = [
    errors.password ? "login-password-error" : null,
    submitError ? "login-submit-error" : null,
  ]
    .filter(Boolean)
    .join(" ") || undefined;

  return (
    <div>
      <h2 className="mb-2 text-2xl font-bold text-gray-900">{t("login.title")}</h2>
      <p className="mb-6 text-gray-600">
        {t("login.subtitle")}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {submitError && (
          <p
            id="login-submit-error"
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            {submitError}
          </p>
        )}

        {/* Email */}
        <div>
          <label htmlFor="email" className="label">
            {t("login.email")}
          </label>
          <input
            {...register("email")}
            type="email"
            id="email"
            autoComplete="email"
            aria-invalid={Boolean(errors.email || submitError)}
            aria-describedby={emailDescribedBy}
            className={`input ${errors.email ? "input-error" : ""}`}
            placeholder="admin@familyhub.local"
          />
          {errors.email && (
            <p id="login-email-error" className="mt-1 text-sm text-red-500">
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between gap-2">
            <label htmlFor="password" className="label">
              {t("login.password")}
            </label>
            {isPasswordResetEnabled() ? (
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                {t("passwordReset.forgotLink")}
              </Link>
            ) : null}
          </div>
          <div className="relative">
            <input
              {...passwordField}
              ref={(element) => {
                passwordRegisterRef(element);
                passwordRef.current = element;
              }}
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              aria-invalid={Boolean(errors.password || submitError)}
              aria-describedby={passwordDescribedBy}
              className={`input pr-10 ${errors.password || submitError ? "input-error" : ""}`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute text-gray-400 -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
              aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <p id="login-password-error" className="mt-1 text-sm text-red-500">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full btn-primary"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t("login.loading")}
            </>
          ) : (
            t("login.submit")
          )}
        </button>
      </form>
    </div>
  );
}
